/**
 * Torn Deal Sniper — entry point.
 *
 * Everything runs in the browser: no backend, no account, no server-side state.
 * Boot order matters a little — the cached item catalog is shown immediately so
 * the typeaheads work offline, then keys are revalidated, then a stale catalog
 * is refreshed once a key is known good.
 */

import "./styles/theme.scss";

import { revalidateAll } from "./api/keyring.ts";
import { loadCachedCatalog, refreshCatalog } from "./data/catalog.ts";
import { bestDeal$, recomputeAll } from "./engine/scheduler.ts";
import { keys$, settings$ } from "./state/store.ts";
import { applyTheme, createHeader } from "./ui/header.ts";
import { createKeysPanel } from "./ui/keys.ts";
import { createSettingsPanel } from "./ui/settings.ts";
import { createStatusPanel } from "./ui/status.ts";
import { createAddItemPanel, createWatchlistPanel } from "./ui/watchlist.ts";
import { createAddRankedPanel, createRankedPanel } from "./ui/ranked.ts";
import { createFooter } from "./ui/privacy.ts";
import { buyUrl } from "./ui/links.ts";
import { append, clear, el, mustFind } from "./ui/dom.ts";

function layout(): HTMLElement {
  const left = el(
    "div",
    { class: "col-12 col-xl-8 d-flex flex-column gap-3" },
    createWatchlistPanel(),
    createAddItemPanel(),
    createRankedPanel(),
    createAddRankedPanel(),
  );

  const right = el(
    "div",
    { class: "col-12 col-xl-4 d-flex flex-column gap-3" },
    createKeysPanel(),
    createSettingsPanel(),
    createStatusPanel(),
  );

  return el("div", { class: "row g-3" }, left, right);
}

/** `B` jumps to the best green deal on screen — the reference app's one hotkey. */
function installHotkeys(): void {
  window.addEventListener("keydown", (event) => {
    if (event.key !== "b" && event.key !== "B") return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return;

    const best = bestDeal$.get();
    if (!best) return;

    event.preventDefault();
    window.open(buyUrl(best.offer), "_blank", "noopener");
  });
}

/**
 * The catalog needs a working key, which on a first visit does not exist until
 * the user pastes one. Watch for the pool going from "nothing usable" to "at
 * least one valid key" and pull the catalog then, so the item search comes
 * alive immediately instead of after a reload.
 */
function autoLoadCatalogOnFirstKey(): void {
  let hadValidKey = false;

  keys$.subscribe((entries) => {
    const hasValidKey = entries.some((entry) => entry.health === "valid");
    if (hasValidKey && !hadValidKey) void refreshCatalog();
    hadValidKey = hasValidKey;
  }, false);
}

async function boot(): Promise<void> {
  const root = mustFind("#app");
  clear(root);

  applyTheme(settings$.get().theme);
  append(root, createHeader(), layout(), createFooter());
  installHotkeys();
  autoLoadCatalogOnFirstKey();

  // Cached catalog first so the item search works before any network call.
  await loadCachedCatalog();

  await revalidateAll();

  // Only worth attempting once a key has proven itself.
  if (keys$.get().some((entry) => entry.health === "valid")) {
    await refreshCatalog();
  }

  recomputeAll();
}

void boot().catch((err) => {
  console.error("[sniper] boot failed", err);
  const root = document.querySelector("#app");
  if (root) {
    root.textContent = `Failed to start: ${err instanceof Error ? err.message : String(err)}`;
  }
});
